import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Copy, Lock, ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { Dialog } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Avatar } from '@/components/ui/avatar';
import { LoadingState, ErrorState } from '@/components/ui/states';
import { usePermission } from '@/Hooks/usePermission';
import { apiMessage } from '../helpers';
import type { PermissionCatalogue, PermissionEntry, Role, RoleDetail } from '../types';

/**
 * §24.3 — the permission vocabulary of the settings screen.
 *
 * The default export is the dialog that edits ONE existing role, but the parts
 * it is built from are exported too, because creating a role from a template
 * needs exactly the same picker and the same preview: two divergent renderings
 * of 124 permission keys is how the two screens end up disagreeing about what
 * `orders.void` means.
 *
 * `GET /permissions` returns the catalogue grouped by resource and
 * `PUT /roles/:id/permissions` takes a full replacement set of KEYS, so the
 * picker's job is to produce that set and the preview's job is to say — in
 * words, not keys — what the set amounts to.
 *
 * Frame `64:2189` restructured this into a page: a role header, a permissions
 * column and an assigned-members rail. That shape is here. What the frame also
 * did was invent its own permission grouping (`Apply discounts` under Orders,
 * `tables` merged with `floor_map`, counts no resource has) and draw away the
 * escalation guard, the system-role lock and the Save button. The catalogue is
 * still generated from `GET /permissions`, and every one of those three is
 * still here — see `PermissionMatrixDialog.test.tsx`.
 */

/* ------------------------------------------------------------------ labels */

/**
 * The catalogue's `description` column is null for every seeded row (the seeder
 * writes `key`/`resource`/`action` only), and an English sentence from the API
 * could not be localized anyway. So the plain-language wording is a client-side
 * catalogue under `settings.permissions.desc.<resource>.<action>`, which gives
 * en/ar parity for free. Falls back to the raw key rather than inventing text.
 */
function permissionLabels(t: TFunction) {
  const resource = (key: string) => t(`settings.permissions.resource.${key}`, key);
  const action = (key: string) => t(`settings.permissions.action.${key}`, key);
  const describe = (permission: PermissionEntry) =>
    t(`settings.permissions.desc.${permission.resource}.${permission.action}`, '');
  return { resource, action, describe };
}

/* --------------------------------------------------------------- catalogue */

export function usePermissionCatalogue(enabled: boolean) {
  const { t } = useTranslation();
  const [catalogue, setCatalogue] = useState<PermissionCatalogue | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || catalogue) return;
    setLoading(true);
    setError(null);
    api
      .get<PermissionCatalogue>('/permissions')
      .then((res) => setCatalogue(res.data))
      .catch((e) => setError(apiMessage(e, t('settings.errors.loadFailed'))))
      .finally(() => setLoading(false));
  }, [enabled, catalogue, t]);

  return { catalogue, loading, error };
}

/** Every key in the set the actor may not hand out — the API refuses all of them. */
export function blockedKeys(selected: Set<string>, canGrant: (key: string) => boolean): string[] {
  return [...selected].filter((key) => !canGrant(key));
}

/* ------------------------------------------------------------------ picker */

interface PickerProps {
  catalogue: PermissionCatalogue | null;
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** `can()` from `usePermission` — already exempts the platform admin. */
  canGrant: (key: string) => boolean;
  readOnly?: boolean;
}

/**
 * 124 keys across 27 resources. A flat checkbox list of that is unreadable, so
 * the picker groups by resource, collapses the groups, searches across the
 * translated label AND the raw key, and keeps a running count — the three
 * things that decide whether someone can actually answer "what does this role
 * have?" without scrolling the whole catalogue.
 *
 * A group opens by default only when the role already holds something in it:
 * 27 open accordions is the wall the grouping exists to remove, and 27 shut ones
 * hides where the grants are. The count badge answers that for the shut ones.
 */
export function PermissionPicker({
  catalogue,
  selected,
  onChange,
  canGrant,
  readOnly = false,
}: PickerProps) {
  const { t } = useTranslation();
  const labels = permissionLabels(t);
  const [filter, setFilter] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string> | null>(null);

  // Seeded once, from the set the role arrived with: later toggles must not
  // make a group the operator just opened snap shut again.
  useEffect(() => {
    if (collapsed !== null || !catalogue) return;
    setCollapsed(
      new Set(
        catalogue.resources
          .filter((group) => group.permissions.every((p) => !selected.has(p.key)))
          .map((group) => group.resource),
      ),
    );
    // `selected` is read as the initial value only; re-running on it would
    // re-seed the accordion on every toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, collapsed]);

  const shut = collapsed ?? new Set<string>();
  const needle = filter.trim().toLowerCase();
  const searching = needle.length > 0 || selectedOnly;

  const groups = useMemo(() => {
    const all = catalogue?.resources ?? [];
    if (!searching) return all;
    return all
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter((permission) => {
          if (selectedOnly && !selected.has(permission.key)) return false;
          if (!needle) return true;
          return (
            permission.key.toLowerCase().includes(needle) ||
            labels.resource(group.resource).toLowerCase().includes(needle) ||
            labels.action(permission.action).toLowerCase().includes(needle) ||
            labels.describe(permission).toLowerCase().includes(needle)
          );
        }),
      }))
      .filter((group) => group.permissions.length > 0);
    // `labels` is rebuilt every render; it is a pure function of `t`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, needle, selectedOnly, selected, searching, t]);

  const setKeys = (keys: string[], on: boolean) => {
    const next = new Set(selected);
    for (const key of keys) {
      if (on) next.add(key);
      else next.delete(key);
    }
    onChange(next);
  };

  const toggleCollapse = (resource: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(resource)) next.delete(resource);
      else next.add(resource);
      return next;
    });

  const total = catalogue?.total ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="max-w-xs"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('settings.roles.filterPermissions')}
          aria-label={t('settings.roles.filterPermissions')}
        />
        <Badge tone="info">
          {t('settings.roles.selectedCount', { selected: selected.size, total })}
        </Badge>
        <label className="flex items-center gap-1.5 text-xs text-ink-muted">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={selectedOnly}
            onChange={(e) => setSelectedOnly(e.target.checked)}
          />
          {t('settings.roles.selectedOnly')}
        </label>
        <div className="ms-auto flex flex-wrap gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setCollapsed(new Set())}
            disabled={shut.size === 0}
          >
            {t('settings.roles.expandAll')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              setCollapsed(new Set((catalogue?.resources ?? []).map((g) => g.resource)))
            }
          >
            {t('settings.roles.collapseAll')}
          </Button>
          {!readOnly && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onChange(new Set())}
              disabled={selected.size === 0}
            >
              {t('settings.roles.clearAll')}
            </Button>
          )}
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="py-8 text-center text-sm text-ink-muted">
          {t('settings.roles.noPermissionsMatch')}
        </p>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => {
            const keys = group.permissions.map((p) => p.key);
            // Select-all never offers what the server would refuse.
            const grantable = keys.filter((key) => canGrant(key));
            const chosen = keys.filter((key) => selected.has(key));
            const allOn = grantable.length > 0 && grantable.every((key) => selected.has(key));
            const open = searching || !shut.has(group.resource);

            return (
              <section key={group.resource} className="rounded-lg border">
                <header className="flex items-center gap-2 p-3">
                  <button
                    type="button"
                    className="flex flex-1 items-center gap-2 text-start"
                    onClick={() => toggleCollapse(group.resource)}
                    aria-expanded={open}
                  >
                    {/* Two glyphs rather than one rotated: `Icon` already knows
                        `ChevronRight` turns around in Arabic, and a rotated
                        chevron would have to re-derive that by hand. */}
                    <Icon as={open ? ChevronDown : ChevronRight} className="shrink-0" />
                    <span className="text-sm font-semibold">{labels.resource(group.resource)}</span>
                    <Badge tone={chosen.length ? 'brand' : 'neutral'}>
                      {chosen.length}/{keys.length}
                    </Badge>
                  </button>
                  {!readOnly && (
                    <label className="flex items-center gap-2 text-xs text-ink-muted">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={allOn}
                        disabled={grantable.length === 0}
                        onChange={(e) => setKeys(grantable, e.target.checked)}
                      />
                      {t('settings.roles.selectAllGroup')}
                    </label>
                  )}
                </header>

                {open && (
                  <div className="grid gap-x-4 gap-y-3 border-t p-3 sm:grid-cols-2 lg:grid-cols-3">
                    {group.permissions.map((permission) => {
                      // The API refuses to grant a key the actor does not hold,
                      // so the refusal is shown here rather than after a save.
                      const blocked = !canGrant(permission.key);
                      const disabled = readOnly || blocked;
                      const description = labels.describe(permission);
                      const reasonId = `grant-${permission.key}`;
                      return (
                        <div
                          key={permission.key}
                          className={`flex items-start gap-3 rounded-md p-1.5 text-sm ${
                            blocked ? 'bg-amber-50/60 dark:bg-amber-950/50' : ''
                          }`}
                          title={blocked ? t('settings.roles.beyondYourGrantReason') : description}
                        >
                          <span className="mt-0.5">
                            <Switch
                              checked={selected.has(permission.key)}
                              disabled={disabled}
                              onChange={(on) => setKeys([permission.key], on)}
                              aria-label={`${labels.resource(group.resource)} · ${labels.action(permission.action)}`}
                              aria-describedby={blocked ? reasonId : undefined}
                            />
                          </span>
                          <span className="min-w-0">
                            <span className="block font-medium">
                              {labels.action(permission.action)}
                            </span>
                            {description && (
                              <span className="block text-xs text-ink-muted">
                                {description}
                              </span>
                            )}
                            <span
                              className="block truncate text-[11px] text-ink-muted"
                              dir="ltr"
                            >
                              {permission.key}
                            </span>
                            {blocked && (
                              <span
                                id={reasonId}
                                className="mt-0.5 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400"
                              >
                                <Icon as={ShieldAlert} size="xs" className="mt-0.5 shrink-0" />
                                {t('settings.roles.beyondYourGrantReason')}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- preview */

/**
 * The matrix is precise and illegible. Before saving, this says the same thing
 * in words — per area, what the role will and will not be able to do — because
 * "Can view, create and send orders · Cannot void or refund" is a sentence a
 * manager can actually check, and 124 ticked boxes is not.
 */
export function PermissionPreview({
  catalogue,
  selected,
}: {
  catalogue: PermissionCatalogue | null;
  selected: Set<string>;
}) {
  const { t } = useTranslation();
  const labels = permissionLabels(t);
  const groups = catalogue?.resources ?? [];

  const granted = groups
    .map((group) => ({
      resource: group.resource,
      can: group.permissions.filter((p) => selected.has(p.key)),
      cannot: group.permissions.filter((p) => !selected.has(p.key)),
    }))
    .filter((group) => group.can.length > 0);

  const untouched = groups.filter((group) => group.permissions.every((p) => !selected.has(p.key)));

  if (granted.length === 0) {
    return (
      <p className="text-sm text-ink-muted">{t('settings.roles.previewEmpty')}</p>
    );
  }

  const list = (items: PermissionEntry[]) =>
    items.map((p) => labels.action(p.action)).join(', ');

  return (
    <div className="space-y-2 text-sm">
      {granted.map((group) => (
        <div key={group.resource} className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium">{labels.resource(group.resource)}</span>
          <span className="text-green-700 dark:text-green-400">
            {t('settings.roles.previewCan')}: {list(group.can)}
          </span>
          {group.cannot.length > 0 && (
            <span className="text-ink-muted">
              · {t('settings.roles.previewCannot')}: {list(group.cannot)}
            </span>
          )}
        </div>
      ))}
      {untouched.length > 0 && (
        <p className="text-xs text-ink-muted">
          {t('settings.roles.previewNoAccess')}:{' '}
          {untouched.map((group) => labels.resource(group.resource)).join(', ')}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ members */

interface RoleAssignmentRow {
  id: string;
  scopeLevel: string;
  scopeId: string | null;
  role: { id: string; key: string; name: string };
}

interface MemberRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  roleAssignments?: RoleAssignmentRow[];
}

/** How many rows the rail asks for; the count beside it says how many there are. */
const MEMBER_PAGE = 10;

/**
 * Who holds this role, and **where**.
 *
 * The frame captions each member with a job title (`Head Waiter`, `Trainee`).
 * `User` has no such column — the model is `{email, phone, username, names,
 * avatarUrl, locale, isActive, lastLoginAt}` — so the caption here is the thing
 * that does exist and matters more: the assignment's `ScopeLevel` (§24.4, "role
 * answers what, scope answers where"). Without it the rail cannot tell a Waiter
 * on Branch A from a Waiter on Floor 2.
 *
 * `GET /users` needs `users.view`, which is not `roles.view` — someone entitled
 * to read the matrix need not be entitled to read the staff list, so the rail
 * says which permission is missing rather than rendering an empty box.
 */
function AssignedMembers({ role }: { role: Role }) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const mayRead = can('users.view');

  const [members, setMembers] = useState<MemberRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(mayRead);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mayRead) return;
    let live = true;
    setLoading(true);
    setError(null);
    api
      .get('/users', { params: { roleId: role.id, limit: MEMBER_PAGE } })
      .then(({ data }) => {
        if (!live) return;
        const payload = data as { data?: MemberRow[]; meta?: { total?: number } } | MemberRow[];
        const rows = Array.isArray(payload) ? payload : (payload?.data ?? []);
        setMembers(rows);
        setTotal(
          Array.isArray(payload) ? rows.length : (payload?.meta?.total ?? rows.length),
        );
      })
      .catch((e) => live && setError(apiMessage(e, t('settings.errors.loadFailed'))))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [mayRead, role.id, t]);

  if (!mayRead) {
    return (
      <p className="text-sm text-ink-muted">
        {t('settings.roles.membersNotPermitted')}
      </p>
    );
  }
  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} />;
  if (members.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        {t('settings.roles.membersEmpty')}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {members.map((member) => {
          const name = [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
          const scopes = (member.roleAssignments ?? [])
            .filter((assignment) => assignment.role.id === role.id)
            .map((assignment) => t(`settings.scope.level.${assignment.scopeLevel}`));
          return (
            <li key={member.id} className="flex items-center gap-3">
              <Avatar src={member.avatarUrl} name={name} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{name || member.id}</span>
                <span className="block truncate text-xs text-ink-muted">
                  {scopes.length ? scopes.join(' · ') : '—'}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      {total > members.length && (
        <p className="text-xs text-ink-muted">
          {t('settings.roles.membersShown', {
            shown: members.length,
            total,
          })}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ dialog */

interface Props {
  open: boolean;
  role: Role | null;
  onClose: () => void;
  onSaved: () => void;
  /** Offered instead of editing when the role is immutable — see §24.2. */
  onDuplicate?: (role: Role) => void;
}

export default function PermissionMatrixDialog({ open, role, onClose, onSaved, onDuplicate }: Props) {
  const { t } = useTranslation();
  const { can } = usePermission();

  const { catalogue, loading: catalogueLoading, error: catalogueError } =
    usePermissionCatalogue(open);
  const [detail, setDetail] = useState<RoleDetail | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isSystem = Boolean(detail?.isSystem ?? role?.isSystem);
  const readOnly = isSystem || !can('roles.update');
  const canGrant = useCallback((key: string) => can(key), [can]);

  useEffect(() => {
    if (!open || !role) return;
    setLoading(true);
    setLoadError(null);
    api
      .get<RoleDetail>(`/roles/${role.id}`)
      .then((res) => {
        setDetail(res.data);
        setSelected(new Set(res.data.permissionKeys ?? []));
      })
      .catch((error) => setLoadError(apiMessage(error, t('settings.errors.loadFailed'))))
      .finally(() => setLoading(false));
  }, [open, role, t]);

  /**
   * The escalation guard checks the WHOLE payload, not the diff — so a role that
   * already holds a key the actor does not is unsavable even untouched. Saying
   * that up front, with a one-click way out, beats a 403 with a key list in it.
   */
  const blocked = useMemo(
    () => (readOnly ? [] : blockedKeys(selected, canGrant)),
    [readOnly, selected, canGrant],
  );

  const save = async () => {
    if (!role) return;
    setSaving(true);
    try {
      await api.put(`/roles/${role.id}/permissions`, { keys: [...selected] });
      toast.success(t('settings.roles.permissionsSaved'));
      onSaved();
      onClose();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const busy = loading || catalogueLoading;
  const error = loadError ?? catalogueError;
  // `_count.assignments` counts assignment ROWS, and one person may hold the
  // same role at several scopes — so it is not a headcount and is not labelled
  // as one (the frame's "12 members assigned" is ambiguous between the two).
  const assignments = detail?._count?.assignments ?? role?._count?.assignments ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={role?.name ?? t('settings.roles.permissionsTitle')}
      description={t('settings.roles.permissionsTitle')}
      className="max-w-6xl"
      footer={
        <>
          {role && onDuplicate && can('roles.create') && (
            <Button
              variant="outline"
              className="me-auto"
              onClick={() => {
                onDuplicate(role);
                onClose();
              }}
            >
              <Icon as={Copy} />
              {t('settings.roles.duplicate')}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {readOnly ? t('common.close') : t('common.cancel')}
          </Button>
          {!readOnly && (
            <Button onClick={() => void save()} disabled={saving || busy || blocked.length > 0}>
              {saving ? t('settings.common.saving') : t('common.save')}
            </Button>
          )}
        </>
      }
    >
      {busy ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} />
      ) : (
        <div className="space-y-4">
          {/* The frame's title row: the role, what kind it is, and how widely it
              is granted — the three facts that decide whether editing is even
              possible. */}
          <div className="flex flex-wrap items-center gap-3">
            {isSystem && <Badge tone="info">{t('settings.roles.systemReadOnly')}</Badge>}
            {assignments !== null && (
              <span className="text-sm text-ink-muted">
                {t('settings.roles.assignmentsTotal', {
                  count: assignments,
                })}
              </span>
            )}
          </div>

          {isSystem && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/50 p-3 text-sm text-sky-900 dark:text-sky-200">
              <Icon as={Lock} className="shrink-0" />
              <span>{t('settings.roles.systemDuplicateHint')}</span>
            </div>
          )}

          {blocked.length > 0 && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-3 text-sm text-amber-900 dark:text-amber-200">
              <p className="flex items-center gap-2 font-medium">
                <Icon as={ShieldAlert} className="shrink-0" />
                {t('settings.roles.blockedTitle')}
              </p>
              <p className="mt-1">{t('settings.roles.blockedHelp', { n: blocked.length })}</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() =>
                  setSelected((prev) => new Set([...prev].filter((key) => canGrant(key))))
                }
              >
                {t('settings.roles.removeBlocked')}
              </Button>
            </div>
          )}

          {/* Frame `64:2189`: permissions left, the people it is granted to
              right. The rail does not stretch — it is context, not a column of
              equal weight — and it stacks under the matrix below `lg`. */}
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
            <section className="min-w-0 space-y-3">
              <h3 className="text-base font-semibold">
                {t('settings.roles.permissionsSection')}
              </h3>
              <PermissionPicker
                catalogue={catalogue}
                selected={selected}
                onChange={setSelected}
                canGrant={canGrant}
                readOnly={readOnly}
              />
            </section>

            <aside className="space-y-3">
              <section className="rounded-lg border p-4">
                <h3 className="mb-3 text-sm font-semibold">
                  {t('settings.roles.membersTitle')}
                </h3>
                {role && <AssignedMembers role={role} />}
              </section>
            </aside>
          </div>

          <section className="rounded-lg border bg-[var(--muted)]/30 p-3">
            <h4 className="mb-2 text-sm font-semibold">{t('settings.roles.previewTitle')}</h4>
            <PermissionPreview catalogue={catalogue} selected={selected} />
          </section>
        </div>
      )}
    </Dialog>
  );
}
